import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { safeStorage } from '../utils/safeStorage';

/**
 * Shell layout state shared by the top bar and the sidebar.
 *
 * The two are siblings in the tree — the bar holds the menu button, the
 * sidebar is what that button opens or collapses — so neither can own the
 * state without the other reaching for it through props. Small enough to
 * be a context of its own rather than a corner of ThemeContext.
 */
const STORAGE_KEY = 'sidebar-collapsed';

interface LayoutContextType {
  /** Desktop: rail of icons only. Persisted per browser. */
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Mobile: the sidebar is an off-canvas drawer. Never persisted. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarCollapsed, setCollapsedState] = useState<boolean>(
    () => safeStorage.getItem(STORAGE_KEY) === '1',
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // The content offset is driven from CSS off this attribute rather than
  // from a React prop: if MainLayout read the collapsed flag, every toggle
  // would re-render the whole page tree under it — a devices table of a
  // thousand rows — just to move a margin.
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar', sidebarCollapsed ? 'collapsed' : 'expanded');
  }, [sidebarCollapsed]);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setCollapsedState(collapsed);
    safeStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed, setSidebarCollapsed]);

  const value = useMemo(
    () => ({ sidebarCollapsed, toggleSidebar, setSidebarCollapsed, mobileNavOpen, setMobileNavOpen }),
    [sidebarCollapsed, toggleSidebar, setSidebarCollapsed, mobileNavOpen],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
